/* eslint-disable @typescript-eslint/naming-convention */
import { PLACEHOLDERS } from "@super-ui/constants";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { CloudinaryOptions } from "@swiggy-private/js-utils/dist/cloudinary/types";
import { imageConfigFormatter } from "@super-ui/helpers/String";
import { IColors, ITypographyV2 } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export const getBackgroundImage = (url: string, options?: CloudinaryOptions) =>
    `url(${getCloudinaryUrl(url, options)})`;

/**
 * Generates a configuration object for a title string, optionally including image configurations.
 *
 * @param titleString - The title string that may contain placeholders for inline images.
 * @param includeImage - A boolean flag indicating whether to include inline images in the title string. Defaults to `false`.
 * @returns An object containing the title string and image configuration.
 *
 * The function processes the `titleString` to identify and replace placeholders for inline images with actual HTML image tags.
 * It uses the `PLACEHOLDERS.INLINE_IMG_START` and `PLACEHOLDERS.INLINE_IMG_END` to locate the image placeholders.
 * If `includeImage` is `true`, the placeholders are replaced with `<img>` tags; otherwise, they are removed from the title string.
 * The image configuration is extracted and formatted using the `imageConfigFormatter` function.
 * Eg: {{img|22px|20px|BLCK/Free%20del.png}}Unlimited Free Deliveries above ₹199}}
 */
export const getTitleConfig = (titleString: string, includeImage = false) => {
    const titleConfig = {
        title: titleString,
        imageConfig: {
            cloudinaryId: "",
            width: "",
            height: "",
        },
    };
    if (titleString.includes(PLACEHOLDERS.INLINE_IMG_START) && titleString.includes(PLACEHOLDERS.INLINE_IMG_END)) {
        // extract all images from descriptionText aand replace them with inline HTML images
        for (let i = 0; i < titleString.length; ) {
            const imageStartIdx = titleString.indexOf(PLACEHOLDERS.INLINE_IMG_START, i);
            const imageEndIdx = titleString.indexOf(PLACEHOLDERS.INLINE_IMG_END) + PLACEHOLDERS.INLINE_IMG_END.length;

            // safety block to prevent issue in case of malformed string
            if (imageStartIdx === -1 || imageEndIdx === -1 || imageEndIdx < imageStartIdx) {
                break;
            }

            const imageString = titleString.substring(imageStartIdx, imageEndIdx);

            const imageConfig = imageConfigFormatter(
                imageString?.replace(PLACEHOLDERS.INLINE_IMG_START, "").replace(PLACEHOLDERS.INLINE_IMG_END, ""),
            );

            const imgTag = `<img src="${getCloudinaryUrl(imageConfig.cloudinaryId)}" width="${
                imageConfig.width
            }" height="${imageConfig.height}"}>`;
            const title = includeImage
                ? titleString.replace(imageString, imgTag)
                : titleString.replace(imageString, "");
            titleConfig.imageConfig = imageConfig;
            titleConfig.title = title;
            i = Math.max(i + 1, imageEndIdx);
        }
    }
    return titleConfig;
};

/**
 * Extracts variant information from a given title string.
 *
 * This function processes a title string to extract variant information
 * based on specific placeholders. If the title is empty, it returns `undefined`.
 * If the title contains a specific placeholder for line breaks, it splits the title
 * into an array of strings. It then maps over the array to extract variant, color,
 * and text information based on typography placeholders.
 *
 * @param {string} title - The title string to extract variant information from.
 * @returns {Array<{ variant: keyof ITypographyV2, color: keyof IColors, text: string }> }
 * An array of objects containing variant, color, and text information, or `undefined` if the title is empty.
 */
export const getVariantFromText = (
    title: string,
): Array<{ variant: keyof ITypographyV2; color: keyof IColors; text: string }> => {
    if (isEmpty(title)) return [];

    const formattedTitle = applyBoldFormatting(title);
    const titleSegments = splitTitle(formattedTitle);

    return titleSegments.map(parseTypographySegment).filter(Boolean);
};

// Helper function to apply bold formatting
const applyBoldFormatting = (title: string): string => {
    return title.replace(PLACEHOLDERS.BOLD_START, "<b>").replace(PLACEHOLDERS.BOLD_END, "</b>");
};

// Helper function to split title based on line break placeholders
const splitTitle = (title: string): string[] => {
    return title.includes(PLACEHOLDERS.BR_TAG) ? title.split(PLACEHOLDERS.BR_TAG) : [title];
};

// Helper function to parse individual typography segments
const parseTypographySegment = (textProp: string) => {
    if (textProp.includes(PLACEHOLDERS.TYPOGRAPHY_START) && textProp.includes(PLACEHOLDERS.TYPOGRAPHY_END)) {
        const styleText = textProp.split(PLACEHOLDERS.TYPOGRAPHY_END)[0].replace(PLACEHOLDERS.TYPOGRAPHY_START, "");
        const [variant, color, text] = styleText.split("|");

        return { variant: variant as keyof ITypographyV2, color: color as keyof IColors, text };
    }

    return {
        variant: "V2.body2.neutral.medium" as keyof ITypographyV2,
        color: "primary" as keyof IColors,
        text: textProp,
    };
};
